import { ChevronLeft, ChevronRight, PaintBucket } from 'lucide-react'
import { RefObject, useEffect, useState } from 'react'
import { ProductVariantColor_ } from 'src/api/db/apiProductVariantColor'

import { Button, Tooltip } from '../../../../ui'
import { Image } from '../../../../ui'
import { cn } from '../../../../ui/utils'

export const ColorVariantSelector = ({
  scrollContainerRef,
  chosen_product_variant_color_,
  product_variant_colors,
  setProduct_variant_color,
}: {
  scrollContainerRef: RefObject<HTMLDivElement>
  chosen_product_variant_color_: ProductVariantColor_
  product_variant_colors: ProductVariantColor_[]
  setProduct_variant_color: (color: ProductVariantColor_) => void
}) => {
  const [showScrollButtons, setShowScrollButtons] = useState(false)

  useEffect(() => {
    const checkScrollNeeded = () => {
      if (scrollContainerRef.current) {
        const container = scrollContainerRef.current
        const hasOverflow = container.scrollWidth > container.clientWidth
        setShowScrollButtons(hasOverflow)
      }
    }

    checkScrollNeeded()
    window.addEventListener('resize', checkScrollNeeded)

    return () => window.removeEventListener('resize', checkScrollNeeded)
  }, [product_variant_colors, scrollContainerRef])

  return (
    <div className="rounded-lg relative group min-w-0">
      {showScrollButtons && (
        <>
          <Button
            variant="ghost"
            size="sm"
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white shadow-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200 h-6 w-6 p-0"
            onClick={() => {
              if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollBy({
                  left: -40,
                  behavior: 'smooth',
                })
              }
            }}
          >
            <ChevronLeft className="w-3 h-3" />
          </Button>

          <Button
            variant="ghost"
            size="sm"
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white shadow-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200 h-6 w-6 p-0"
            onClick={() => {
              if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollBy({
                  left: 40,
                  behavior: 'smooth',
                })
              }
            }}
          >
            <ChevronRight className="w-3 h-3" />
          </Button>
        </>
      )}

      <div
        ref={scrollContainerRef}
        className="overflow-x-auto scrollbar-hide min-w-0"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        <div className="flex gap-1 h-[38px] items-center">
          {product_variant_colors?.map((colorVariant) => {
            const { id } = colorVariant
            let illustrations = colorVariant.product_image ?? []
            illustrations = illustrations.sort(
              (a, b) => (a?.order ?? 0) - (b?.order ?? 0)
            )
            const image_url = illustrations?.[0]?.image_url

            const isSelected = chosen_product_variant_color_?.id === id

            return (
              <Tooltip
                content={
                  <span className="text-sm flex items-center">
                    <PaintBucket className="w-3 h-3 mr-1.5" />
                    {colorVariant.color}
                  </span>
                }
                delayDuration={0}
              >
                <div
                  key={id}
                  className={cn(
                    'bg-white rounded-sm flex items-center justify-center border border-white flex-shrink-0 relative',
                    isSelected && 'border border-gray-400'
                  )}
                >
                  <Image
                    src={image_url || ''}
                    className="w-8 h-8 object-contain cursor-pointer flex-shrink-0 p-1"
                    onClick={() => {
                      setProduct_variant_color(colorVariant)
                    }}
                  />
                  {isSelected && (
                    <div className="absolute inset-0 bg-gray-300/40 rounded-sm flex items-center justify-center"></div>
                  )}
                </div>
              </Tooltip>
            )
          })}
        </div>
      </div>
    </div>
  )
}
